import React, { useState, useEffect, useReducer } from "react";
import openSocket from "../../services/socket-io";

import {
  Hidden,
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  InputAdornment,
  TextField,
} from "@material-ui/core";
import { Edit, DeleteOutline, AddBox, Search } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import LocaleServerModal from "../../components/LocaleServerModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

const reducer = (state, action) => {
  if (action.type === "LOAD") {
    const localeServers = action.payload;
    const newLocaleServers = [];

    localeServers.forEach((localeServer) => {
      const index = state.findIndex((q) => q.id === localeServer.id);
      if (index !== -1) {
        state[index] = localeServer;
      } else {
        newLocaleServers.push(localeServer);
      }
    });

    return [...state, ...newLocaleServers];
  }

  if (action.type === "UPDATE") {
    const localeServer = action.payload;
    const index = state.findIndex((q) => q.id === localeServer.id);

    if (index !== -1) {
      state[index] = localeServer;
      return [...state];
    }

    return [localeServer, ...state];
  }

  if (action.type === "DELETE") {
    const localeServerId = action.payload;
    const index = state.findIndex((q) => q.id === localeServerId);
    if (index !== -1) {
      state.splice(index, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const LocaleServers = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [localeServers, dispatch] = useReducer(reducer, []);
  const [selectedLocaleServer, setSelectedLocaleServer] = useState(null);
  const [localeServerModalOpen, setLocaleServerModalOpen] = useState(false);
  const [deletingLocaleServer, setDeletingLocaleServer] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchLocaleServers = async () => {
        try {
          const { data } = await api.get("/localeServers/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD", payload: data.localeServers });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchLocaleServers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("localeServer", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE", payload: data.localeServer });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE", payload: +data.localeServerId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenModal = () => {
    setSelectedLocaleServer(null);
    setLocaleServerModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedLocaleServer(null);
    setLocaleServerModalOpen(false);
  };

  const handleEdit = (localeServer) => {
    setSelectedLocaleServer(localeServer);
    setLocaleServerModalOpen(true);
  };

  const handleDelete = async (localeServerId) => {
    try {
      await api.delete(`/localeServers/${localeServerId}`);
      toast.success(i18n.t("global.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingLocaleServer(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingLocaleServer && `${i18n.t("global.deleteTitle")} ${deletingLocaleServer.name}?`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDelete(deletingLocaleServer.id)}
      >
        {i18n.t("global.deleteMessage")}
      </ConfirmationModal>
      <LocaleServerModal
        open={localeServerModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="form-dialog-title"
        localeServerId={selectedLocaleServer && selectedLocaleServer.id}
      />

      <MainHeader>
        <Title>Locale Servers</Title>
        <MainHeaderButtonsWrapper>
          <Hidden mdDown>
            <TextField
              placeholder={i18n.t("global.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Hidden>
          <Button variant="contained" color="primary" onClick={handleOpenModal}>
            <AddBox /> <Hidden mdDown>{i18n.t("global.buttons.add")}</Hidden>
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Hidden lgUp>
        <TextField
          placeholder={i18n.t("global.searchPlaceholder")}
          type="search"
          value={searchParam}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search style={{ color: "gray" }} />
              </InputAdornment>
            ),
          }}
        />
        <br />
      </Hidden>

      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Nome</TableCell>
              <TableCell align="center">Endereço</TableCell>
              <TableCell align="center">Backend Dir</TableCell>
              <TableCell align="center">Chrome Bin</TableCell>
              <TableCell align="center">Enabled</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Token</TableCell>
              <TableCell align="center">{i18n.t("global.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {localeServers.map((localeServer) => (
                <TableRow key={localeServer.id}>
                  <TableCell align="center">{localeServer.name}</TableCell>
                  <TableCell align="center">{localeServer.address}</TableCell>
                  <TableCell align="center">{localeServer.backendDir}</TableCell>
                  <TableCell align="center">{localeServer.CHROME_BIN}</TableCell>
                  <TableCell align="center">{localeServer.enabled ? "✅" : "❌"}</TableCell>
                  <TableCell align="center">{localeServer.status ? "✅" : "❌"}</TableCell>
                  <TableCell align="center">{localeServer.token}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEdit(localeServer)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" onClick={() => { setConfirmModalOpen(true); setDeletingLocaleServer(localeServer); }}>
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={8} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default LocaleServers;
